<template>
  <div
    id="contact-us"
    class="contact-us critical-component-clear"
    :style="{
      '--section-spacing': spacing,
      '--section-spacing-l': spacingDesktop
    }"
  >
    <div class="container">
      <div class="grid">
        <div class="col xs4 l2">
          <component
            :is="liquid.sectionIndex === 0 ? 'h1' : 'h2'"
            v-if="title"
            :id="`section-title-${liquid.sectionId}`"
            class="
              contact-us__title
              text-heading-2xl
              text-heading-5xl-desktop
            "
          >
            <span v-text="title" />
          </component>
        </div>

        <div class="contact-us__body col xs4 l4-9">
          <p
            class="
              contact-us__text
              text-body-s
              text-body-m-desktop
            "
            v-html="text"
          />

          <p
            v-if="success"
            class="
              contact-us__success-msg
              text-body-s-bold
              text-body-m-bold-desktop
            "
          >
            <icon-tick />
            <span v-text="$string('contact_form.success_msg')" />
          </p>

          <form
            id="contact_form"
            method="post"
            action="/contact#contact-us"
            accept-charset="UTF-8"
            class="contact-us__form"
            @submit.prevent="onSubmit"
          >
            <input
              type="hidden"
              name="form_type"
              value="contact"
            >

            <input
              type="hidden"
              name="return_to"
              value="/pages/contact?contact_posted=true#contact-us"
            >

            <div class="contact-us__form-group">
              <label
                class="text-body-m"
                for="contact_form-name"
                v-text="$string('contact_form.name') + '*'"
              />

              <input
                id="contact_form-name"
                autocomplete="name"
                type="text"
                name="contact[name]"
                :aria-invalid="error.field === 'name'"
                :aria-describedby="error.field === 'name' ? 'contact-us-name-error' : ''"
                aria-required
                @focus="focusHandler"
                @blur="blurHandler"
                @input="error.field === 'name' ? error.field = '' : null"
              >

              <p
                v-if="error.field === 'name'"
                id="contact-us-name-error"
                class="contact-us__error-msg text-body-s"
                v-text="$string('contact_form.name_error')"
              />
            </div>

            <div class="contact-us__form-group">
              <label
                class="text-body-m"
                for="contact_form-email"
                v-text="$string('contact_form.email') + '*'"
              />

              <input
                id="contact_form-email"
                type="email"
                name="contact[email]"
                autocorrect="off"
                autocapitalize="off"
                autocomplete="email"
                :aria-invalid="error.field === 'email'"
                :aria-describedby="error.field === 'email' ? 'contact-us-email-error' : ''"
                aria-required
                @focus="focusHandler"
                @blur="blurHandler"
                @input="error.field === 'email' ? error.field = '' : null"
              >

              <p
                v-if="error.field === 'email'"
                id="contact-us-email-error"
                class="contact-us__error-msg text-body-s"
                v-text="$string('contact_form.email_error')"
              />
            </div>

            <div class="contact-us__form-group">
              <label
                class="text-body-m"
                for="contact_form-order"
                v-text="$string('contact_form.order')"
              />

              <input
                id="contact_form-order"
                type="text"
                name="contact[order]"
                @focus="focusHandler"
                @blur="blurHandler"
              >
            </div>

            <div class="contact-us__form-group contact-us__msg">
              <label
                class="contact-us__msg-label text-body-m"
                for="contact_form-message"
                v-text="$string('contact_form.message')"
              />

              <textarea
                id="contact_form-message"
                class="contact-us__msg-value text-body-m"
                name="contact[body]"
                :aria-invalid="error.field === 'msg'"
                :aria-describedby="error.field === 'msg' ? 'contact-us-msg-error' : ''"
                aria-required
                :placeholder="$string('contact_form.message_placeholder')"
                @input="error.field === 'msg' ? error.field = '' : null"
              />

              <p
                v-if="error.field === 'msg'"
                id="contact-us-msg-error"
                class="contact-us__error-msg text-body-s"
                v-text="$string('contact_form.msg_error')"
              />
            </div>

            <btn
              class="contact-us__cta"
              :block-class="contact-us"
              :label="$string('contact_form.submit')"
              modifiers="boxed"
              type="button"
              @click="onSubmit"
            />
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Contact Us  (contact-us)
 * -----------------------------------------------------------------------------
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Number} liquid.sectionIndex - Section's 0 based index.
 * @param {String} title - The section title.
 * @param {String} text - The section text.
 * @param {String} spacing - Custom spacing value.
 * @param {String} spacingDesktop - Custom spacing value for desktop.
 *
 */
import Btn from '~global/btn/btn'
import IconTick from '~icons/misc/tick.svg'

export default {
  name: 'ContactUs',

  components: {
    Btn,
    IconTick,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },

    title: {
      type: String,
      default: '',
    },

    text: {
      type: String,
      default: '',
    },

    spacing: {
      type: String,
      default: '',
    },

    spacingDesktop: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      error: {
        field: '',
      },
      success: false,
    }
  },

  mounted() {
    const urlParams = new URLSearchParams(window.location.search)

    if (urlParams.get('contact_posted') === 'true') {
      this.success = true
    }
  },

  methods: {
    focusHandler(event) {
      const input = event.currentTarget
      input.parentElement.classList.add('focused')
    },
    blurHandler(event) {
      const input = event.currentTarget
      if (!input.value) {
        input.parentElement.classList.remove('focused')
      }
    },
    validEmail(email = '') {
      // eslint-disable-next-line max-len
      // eslint-disable-next-line prefer-named-capture-group, no-useless-escape
      const emailRegex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return emailRegex.test(email)
    },
    onSubmit(event) {
      event.preventDefault()
      event.stopPropagation()

      const form = event.target.closest('form')
      const formData = new FormData(form)

      const name = formData.get('contact[name]')
      const email = formData.get('contact[email]')
      const msg = formData.get('contact[body]')

      if (!name) {
        this.error.field = 'name'
        return
      }

      if (!this.validEmail(email)) {
        this.error.field = 'email'
        return
      }

      if (!msg) {
        this.error.field = 'msg'
        return
      }

      form.submit()
    },
  },
}
</script>

<style lang="scss">
@import './contact-us';
</style>
